/**
 * IconPicker — Modal dialog for choosing a custom icon on a container.
 *
 * Supports pikaicons (SVG) and emoji. Search + category filtering.
 * Uses the same Dialog pattern as TagPicker.
 *
 * Icon string format (prefix-routed):
 *   "pika:category/icon-name"  → pikaicons SVG
 *   "emoji:☕"                 → Unicode emoji
 *   undefined                  → reset to default TimelineIcon
 */

import {
  memo,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react"
import { useTranslation } from "react-i18next"
import { useLiveQueryImmediate } from "@/db/hooks"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/app/components/ui/dialog"
import { cn } from "@/shared/utils/general/cn"
import catalog from "@/shared/icons/pikaicons-catalog.json"
import { updateContainerIcon } from "@/db/operations"
import { db } from "@/db/schema"
import { PIKA_STYLES, PikaIcon, ensurePikaImport, type PikaStyle } from "@/shared/icons/resolveContainerIcon"
import SearchIcon from "@/shared/icons/totodoicons/stroke/search.svg?react"
import ChevronLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-left.svg?react"
import ChevronRightIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-right.svg?react"
import RotateIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/rotate-left.svg?react"
import { EMOJI_GROUPS, type EmojiEntry } from "./emoji-data"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IconPickerProps {
  containerId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When provided, called with the selected icon value instead of updating a container */
  onIconSelected?: (icon: string | undefined) => void
  /** Current icon value (used when no containerId is provided) */
  currentIconOverride?: string
}

interface CatalogIcon {
  name: string
  displayName: string
  category: string
  styles: string[]
}

type Tab = "icons" | "emoji"

// ---------------------------------------------------------------------------
// Style preview icon — shows a heart in each style as button content
// ---------------------------------------------------------------------------

const StylePreviewIcon = memo(function StylePreviewIcon({ style }: { style: PikaStyle }) {
  return <PikaIcon categoryAndName="general/heart" pikaStyle={style} className="size-4" />
})

// ---------------------------------------------------------------------------
// Category list from catalog
// ---------------------------------------------------------------------------

const categories = (catalog.categories as Array<{ name: string; count: number }>).map(
  (c) => c.name,
)

const allIcons = catalog.icons as CatalogIcon[]

// ---------------------------------------------------------------------------
// Derived emoji data from comprehensive dataset
// ---------------------------------------------------------------------------

const emojiCategories = EMOJI_GROUPS.map((g) => g.name)
const allEmojis = EMOJI_GROUPS.flatMap((g) =>
  g.items.map((item) => ({ ...item, category: g.name })),
)
type FlatEmoji = EmojiEntry & { category: string }

// ---------------------------------------------------------------------------
// Virtualized icon grid (8 columns, ~40px rows)
// ---------------------------------------------------------------------------

const COLS = 8
const ROW_HEIGHT = 40 // p-2 (8+8) + size-5 (20) + gap contribution ≈ 40
const GAP = 4 // gap-1

const VirtualIconGrid = memo(function VirtualIconGrid({
  icons,
  currentIcon,
  onSelect,
  style: pikaStyle = "stroke",
}: {
  icons: CatalogIcon[]
  currentIcon: string | undefined
  onSelect: (icon: CatalogIcon) => void
  style?: PikaStyle
}) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Preload the first batch of icons on mount / when filtered list changes
  useEffect(() => {
    const batch = icons.slice(0, COLS * 12) // preload first ~12 rows
    for (const icon of batch) {
      ensurePikaImport(`${icon.category}/${icon.name}`, pikaStyle)
    }
  }, [icons, pikaStyle])

  const rowCount = Math.ceil(icons.length / COLS)

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    gap: GAP,
    overscan: 5,
  })

  if (icons.length === 0) {
    return (
      <div className="flex-1 p-3">
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t("No icons found")}
        </p>
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-3">
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const startIdx = virtualRow.index * COLS
          const rowIcons = icons.slice(startIdx, startIdx + COLS)

          return (
            <div
              key={virtualRow.index}
              className="absolute left-0 right-0 grid grid-cols-8 gap-1"
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {rowIcons.map((icon) => {
                const key = `${icon.category}/${icon.name}`
                const iconStr = pikaStyle === "stroke"
                  ? `pika:${key}`
                  : `pika:${pikaStyle}:${key}`
                const isSelected = currentIcon === iconStr
                return (
                  <button
                    key={key}
                    className={cn(
                      "flex items-center justify-center rounded-lg p-2 transition-colors hover:bg-primary/10",
                      isSelected && "bg-primary/15 ring-1 ring-primary/30",
                    )}
                    onClick={() => onSelect(icon)}
                    title={icon.displayName}
                  >
                    <PikaIcon categoryAndName={key} pikaStyle={pikaStyle} className="size-5" />
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Scrollable container with fade gradients at edges
// ---------------------------------------------------------------------------

const ScrollFadeContainer = memo(function ScrollFadeContainer({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeft(el.scrollLeft > 4)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    // RAF ensures the browser has laid out children before we measure
    const frame = requestAnimationFrame(checkScroll)
    const observer = new ResizeObserver(checkScroll)
    if (scrollRef.current) observer.observe(scrollRef.current)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [checkScroll, children]) // re-check when children change (category filtering)

  return (
    <div className={cn("relative shrink-0 border-b border-border", className)}>
      <div
        ref={scrollRef}
        className="flex gap-1 overflow-x-auto px-3 py-2 scrollbar-none"
        onScroll={checkScroll}
      >
        {children}
      </div>
      {showLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => {
            const el = scrollRef.current
            if (el) el.scrollBy({ left: -el.clientWidth * 0.75, behavior: "smooth" })
          }}
          className="absolute inset-y-0 left-0 z-10 flex w-8 cursor-pointer items-center bg-gradient-to-r from-background from-40% to-transparent"
        >
          <ChevronLeftIcon className="size-4 text-muted-foreground" />
        </button>
      )}
      {showRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => {
            const el = scrollRef.current
            if (el) el.scrollBy({ left: el.clientWidth * 0.75, behavior: "smooth" })
          }}
          className="absolute inset-y-0 right-0 z-10 flex w-8 cursor-pointer items-center justify-end bg-gradient-to-l from-background from-40% to-transparent"
        >
          <ChevronRightIcon className="size-4 text-muted-foreground" />
        </button>
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const IconPicker = memo(function IconPicker({
  containerId,
  open,
  onOpenChange,
  onIconSelected,
  currentIconOverride,
}: IconPickerProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>("icons")
  const [query, setQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [emojiCategory, setEmojiCategory] = useState<string | null>(null)
  const [customEmoji, setCustomEmoji] = useState("")
  const [iconStyle, setIconStyle] = useState<PikaStyle>("stroke")
  const inputRef = useRef<HTMLInputElement>(null)

  // Live-query the current icon from DB
  const [dbIcon] = useLiveQueryImmediate(
    () => (containerId ? db.totodo.get(containerId).then((o) => o?.icon) : undefined),
    ["icon-picker", containerId],
    undefined,
  )
  const currentIcon = currentIconOverride ?? dbIcon

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery("")
      setSelectedCategory(null)
      setEmojiCategory(null)
      setCustomEmoji("")
      setTab("icons")
      // Focus search input after dialog animation
      const t = setTimeout(() => inputRef.current?.focus(), 100)
      return () => clearTimeout(t)
    }
  }, [open])

  // Filter icons by search + category + style availability
  const filteredIcons = useMemo(() => {
    let icons = allIcons
    // Only show icons available in the selected style
    if (iconStyle !== "stroke") {
      icons = icons.filter((i) => i.styles.includes(iconStyle))
    }
    if (selectedCategory) {
      icons = icons.filter((i) => i.category === selectedCategory)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      icons = icons.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.displayName.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q),
      )
    }
    return icons
  }, [query, selectedCategory, iconStyle])

  // Filter emojis by search + category
  const filteredEmojis = useMemo(() => {
    let emojis: FlatEmoji[] = allEmojis
    if (emojiCategory) {
      emojis = emojis.filter((e) => e.category === emojiCategory)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      emojis = emojis.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.emoji.includes(q) ||
          e.category.toLowerCase().includes(q),
      )
    }
    return emojis
  }, [query, emojiCategory])

  const handleSelectPika = useCallback(
    (icon: CatalogIcon) => {
      const value = iconStyle === "stroke"
        ? `pika:${icon.category}/${icon.name}`
        : `pika:${iconStyle}:${icon.category}/${icon.name}`
      if (onIconSelected) {
        onIconSelected(value)
      } else if (containerId) {
        void updateContainerIcon(containerId, value)
      }
      onOpenChange(false)
    },
    [containerId, onOpenChange, onIconSelected, iconStyle],
  )

  const handleSelectEmoji = useCallback(
    (emoji: string) => {
      const value = `emoji:${emoji}`
      if (onIconSelected) {
        onIconSelected(value)
      } else if (containerId) {
        void updateContainerIcon(containerId, value)
      }
      onOpenChange(false)
    },
    [containerId, onOpenChange, onIconSelected],
  )

  const handleReset = useCallback(() => {
    if (onIconSelected) {
      onIconSelected(undefined)
    } else if (containerId) {
      void updateContainerIcon(containerId, undefined)
    }
    onOpenChange(false)
  }, [containerId, onOpenChange, onIconSelected])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(600px,85vh)] w-full max-w-md flex-col gap-0 p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{t("Choose Icon")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("Select a custom icon for this container")}
        </DialogDescription>

        {/* ── Header: tabs + search ── */}
        <div className="flex flex-col gap-2 border-b border-border p-3 pb-2">
          {/* Tabs + style selector */}
          <div className="flex items-center gap-1">
            <button
              className={cn(
                "rounded-md px-3 py-1 text-sm font-medium transition-colors",
                tab === "icons"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setTab("icons")}
            >
              {t("Icons")}
            </button>
            <button
              className={cn(
                "rounded-md px-3 py-1 text-sm font-medium transition-colors",
                tab === "emoji"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setTab("emoji")}
            >
              {t("Emoji")}
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Style button group — only when on icons tab */}
            {tab === "icons" && (
              <div className="flex items-center rounded-md border border-border">
                {PIKA_STYLES.map((s) => (
                  <button
                    key={s}
                    className={cn(
                      "px-1.5 py-1 text-[10px] font-medium capitalize transition-colors first:rounded-l-[5px] last:rounded-r-[5px]",
                      iconStyle === s
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setIconStyle(s)}
                    title={s}
                  >
                    <StylePreviewIcon style={s} />
                  </button>
                ))}
              </div>
            )}

            {/* Reset button — only visible when a custom icon is set */}
            {currentIcon && (
              <button
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={handleReset}
              >
                <RotateIcon className="size-3.5" />
                {t("Reset")}
              </button>
            )}
          </div>

          {/* Search input — shared across both tabs */}
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === "icons" ? t("Search icons…") : t("Search emoji…")}
              className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* ── Icons tab ── */}
        {tab === "icons" && (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Category pills — horizontal scrollable with fade indicators */}
            <ScrollFadeContainer>
              <button
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                  selectedCategory === null
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setSelectedCategory(null)}
              >
                {t("All")}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={cn(
                    "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize transition-colors",
                    selectedCategory === cat
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.replace(/-/g, " ")}
                </button>
              ))}
            </ScrollFadeContainer>

            {/* Icon grid — virtualized */}
            <VirtualIconGrid
              icons={filteredIcons}
              currentIcon={currentIcon}
              onSelect={handleSelectPika}
              style={iconStyle}
            />
          </div>
        )}

        {/* ── Emoji tab ── */}
        {tab === "emoji" && (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Emoji category pills with fade indicators */}
            <ScrollFadeContainer>
              <button
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                  emojiCategory === null
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setEmojiCategory(null)}
              >
                {t("All")}
              </button>
              {emojiCategories.map((cat) => {
                const group = EMOJI_GROUPS.find((g) => g.name === cat)
                return (
                  <button
                    key={cat}
                    className={cn(
                      "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                      emojiCategory === cat
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setEmojiCategory(cat)}
                  >
                    {group?.icon} {cat}
                  </button>
                )
              })}
            </ScrollFadeContainer>

            {/* Custom emoji input */}
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5">
              <span className="text-xs text-muted-foreground">{t("Or paste:")}</span>
              <input
                type="text"
                value={customEmoji}
                onChange={(e) => setCustomEmoji(e.target.value)}
                placeholder="😀"
                className="h-6 w-14 rounded border border-border bg-background text-center text-lg outline-none focus:ring-1 focus:ring-primary/30"
                maxLength={4}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customEmoji.trim()) {
                    handleSelectEmoji(customEmoji.trim())
                  }
                }}
              />
              <button
                className="rounded px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-40"
                disabled={!customEmoji.trim()}
                onClick={() => customEmoji.trim() && handleSelectEmoji(customEmoji.trim())}
              >
                {t("Use")}
              </button>
            </div>

            {/* Emoji grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {filteredEmojis.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t("No emoji found")}
                </p>
              ) : (
                <div className="grid grid-cols-8 gap-1">
                  {filteredEmojis.map((item, i) => {
                    const isSelected = currentIcon === `emoji:${item.emoji}`
                    return (
                      <button
                        key={`${item.emoji}-${i}`}
                        className={cn(
                          "flex items-center justify-center rounded-lg p-2 text-xl transition-colors hover:bg-primary/10",
                          isSelected && "bg-primary/15 ring-1 ring-primary/30",
                        )}
                        onClick={() => handleSelectEmoji(item.emoji)}
                        title={item.name}
                      >
                        {item.emoji}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
})
